<template>
  <section class="product-product-section" v-if="currentProductData && currentProductData.length > 0">
    <div class="container">
        <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <p class="loading-text">{{ t('common.loading') }}</p>
      </div>
      <!-- 错误状态 -->
      <div v-else-if="errorMessage" class="error-container">
        <div class="error-icon">
          <el-icon size="60"><Warning /></el-icon>
        </div>
        <h3 class="error-title">{{ t('common.error') }}</h3>
        <p class="error-message">{{ errorMessage }}</p>
        <button class="retry-button" @click="fetchPageData">
          {{ t('common.retry') }}
        </button>
      </div>
      <div class="active-product-display animate__animated animate__fadeIn" :key="activeIndex">
        <div class="product-detail-card">
          <div class="product-image-container">
            <!-- <div class="product-main-image"> -->
              <el-image :src="activeProduct.big_image" fit="cover">
                <template #error>
                  <div class="image-placeholder">
                    <span>{{ activeProduct.name }}</span>
                  </div>
                </template>
              </el-image>
            <!-- </div> -->
          </div>

          <div class="product-content-container">
            <h2 class="product-title">{{ activeProduct.name }}</h2>
            
            <div class="product-description">
              <p>{{ activeProduct.description }}</p>
            </div>

            <div class="info-grid">
              <!-- 暂时注释掉冶炼技术和产量概况
              <div class="info-item">
                <div class="info-header">
                  <i class="el-icon-cpu"></i>
                  <h4>{{ t('ore.tech_title') || '冶炼技术' }}</h4>
                </div>
                <p>{{ activeProduct.tech_intro }}</p>
              </div>
              <div class="info-item">
                <div class="info-header">
                  <i class="el-icon-data-line"></i>
                  <h4>{{ t('ore.output_title') || '产量概况' }}</h4>
                </div>
                <p>{{ activeProduct.production_intro }}</p>
              </div>
              -->
              <div class="info-item" v-if="activeProduct.application">
                <div class="info-header">
                  <i class="el-icon-setting"></i>
                  <h4>{{ t('ore.application_title') || '主要应用' }}</h4>
                </div>
                <p>{{ activeProduct.application }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="product-list-container">
        <button 
          class="nav-btn nav-prev" 
          @click="scrollPrev"
          :disabled="scrollPosition === 0"
        >
          <el-icon><ArrowLeft /></el-icon>
        </button>
        
        <div class="product-list-viewport" ref="listViewport">
          <div 
            class="product-list-wrapper" 
            :style="{ 
              transform: `translateX(calc(-${getItemOffset(scrollPosition)}px + ${dragOffset}px))`,
              transition: isDragging ? 'none' : 'transform 0.4s ease'
            }"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
          >
            <div
              v-for="(item, index) in currentProductData"
              :key="index"
              class="product-thumb-item"
              :class="{ 'active': activeIndex === index }"
              @click="handleItemClick(index)"
            >
              <div class="thumb-img-box">
                <el-image :src="item.raw_image" fit="cover">
                  <template #error>
                    <div class="image-placeholder">
                      <span>{{ item.name }}</span>
                    </div>
                  </template>
                </el-image>
              </div>
              <span class="thumb-name">{{ item.name }}</span>
            </div>
          </div>
        </div>
        
        <button 
          class="nav-btn nav-next" 
          @click="scrollNext"
          :disabled="scrollPosition >= maxScrollPosition"
        >
          <el-icon><ArrowRight /></el-icon>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue';
import { getSinglePageContent } from "@/assets/api/website";



const isLoading = ref(false)
const errorMessage = ref('')
const { t, locale } = useI18n();
const activeIndex = ref(0);
const scrollPosition = ref(0);
const listViewport = ref(null);
let autoPlayTimer = null;
const productionsDataConfigs = ref([]);

const viewportWidth = ref(840);
const itemWidth = ref(120);
const itemGap = ref(20);

const getResponsiveItemWidth = () => {
  const width = window.innerWidth;
  if (width <= 576) {
    return 90;
  }
  return 120;
};

const getResponsiveItemGap = () => {
  const width = window.innerWidth;
  if (width <= 576) {
    return 10;
  }
  return 20;
};

const getResponsiveViewportWidth = () => {
  const width = window.innerWidth;
  if (width <= 576) {
    return 280;
  } else if (width <= 768) {
    return 560;
  } else if (width <= 992) {
    return 700;
  }
  return 840;
};

const visibleCount = computed(() => {
  const currentWidth = itemWidth.value + itemGap.value;
  return Math.floor(viewportWidth.value / currentWidth);
});

const updateResponsiveValues = () => {
  viewportWidth.value = getResponsiveViewportWidth();
  itemWidth.value = getResponsiveItemWidth();
  itemGap.value = getResponsiveItemGap();
};

let touchStartX = 0;
let touchEndX = 0;
const isDragging = ref(false);
const dragOffset = ref(0);

watch(locale, () => {
  activeIndex.value = 0;
  scrollPosition.value = 0;
});

const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''
  console.log('fetchPageData', locale.value)
  try {
    const currentLocale = locale.value
    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/products'
    })

    if (response && response.content) {
      productionsDataConfigs.value = response.content.list || []
    } else {
      productionsDataConfigs.value = []
    }
    
    startAutoPlay();
  } catch (error) {
    console.error('获取数据失败：', error)
    errorMessage.value = t('common.load_failed', '加载失败')
  } finally {
    isLoading.value = false
  }
}
const maxScrollPosition = computed(() => {
  const total = currentProductData.value?.length || 0;
  return Math.max(0, total - visibleCount.value);
});

const getItemOffset = (index) => {
  return index * (itemWidth.value + itemGap.value);
};

const handleTouchStart = (e) => {
  touchStartX = e.touches[0].clientX;
  isDragging.value = true;
  dragOffset.value = 0;
};

const handleTouchMove = (e) => {
  if (!isDragging.value) return;
  touchEndX = e.touches[0].clientX;
  dragOffset.value = touchEndX - touchStartX;
};

const handleTouchEnd = () => {
  if (!isDragging.value) return;
  isDragging.value = false;
  
  const swipeThreshold = 50;
  const swipeDistance = touchEndX - touchStartX;
  
  if (Math.abs(swipeDistance) > swipeThreshold) {
    if (swipeDistance > 0) {
      scrollPrev();
    } else {
      scrollNext();
    }
  }
  
  dragOffset.value = 0;
};

const updateScrollToCenter = (index) => {
  const centerOffset = Math.floor(visibleCount.value / 2);
  const targetPosition = index - centerOffset;
  scrollPosition.value = Math.max(0, Math.min(targetPosition, maxScrollPosition.value));
};

const scrollPrev = () => {
  if (scrollPosition.value > 0) {
    scrollPosition.value--;
  }
};

const scrollNext = () => {
  if (scrollPosition.value < maxScrollPosition.value) {
    scrollPosition.value++;
  }
};

const handleItemClick = (index) => {
  activeIndex.value = index;
  resetAutoPlay();
  updateScrollToCenter(index);
};

const startAutoPlay = () => {
  stopAutoPlay();
  autoPlayTimer = setInterval(() => {
    const nextIndex = (activeIndex.value + 1) % currentProductData.value.length;
    activeIndex.value = nextIndex;
    updateScrollToCenter(nextIndex);
  }, 5000);
};

const stopAutoPlay = () => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer);
    autoPlayTimer = null;
  }
};

const resetAutoPlay = () => {
  stopAutoPlay();
  startAutoPlay();
};

onMounted(() => {
  updateResponsiveValues();
  window.addEventListener('resize', updateResponsiveValues);
  fetchPageData()
});

onUnmounted(() => {
  stopAutoPlay();
  window.removeEventListener('resize', updateResponsiveValues);
});


const currentProductData = computed(() => productionsDataConfigs.value);
const activeProduct = computed(() => currentProductData.value[activeIndex.value] || {});
// 监听语言变化，重新加载数据
watch(
  () => locale.value,
  () => {
    fetchPageData()
  }
);
</script>
<style lang="scss" scoped>
@import "@/assets/css/common";

.product-product-section {
  padding: 0;

  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .active-product-display {
    margin-bottom: 40px;

    .product-detail-card {
      display: flex;
      background: #fff;
      border-radius: 20px;
      box-shadow: 0 15px 40px rgba(0, 0, 0, 0.06);
      overflow: hidden;
      min-height: 400px;
      @media (min-width: 992px) {
        max-height: 400px;
      }
      @media (max-width: 992px) {
        flex-direction: column;
        min-height: auto;
      }
    }

    .product-image-container {
      flex: 0 0 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      // padding: 40px;
      background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);

      @media (max-width: 992px) {
        flex: none;
        // padding: 30px;
      }

      // width: 100%;
      max-width: 400px;
      aspect-ratio: 1 / 1;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

      @media (max-width: 992px) {
        max-width: 300px;
        margin: 20px auto;
      }

      @media (max-width: 576px) {
        max-width: 250px;
        margin: 20px auto;
      }

      .el-image {
        width: 100%;
        height: 100%;
      }
    }

    .product-content-container {
      flex: 1;
      padding: 40px;
      display: flex;
      flex-direction: column;
      // justify-content: center;
      overflow-y: auto;

      @media (max-width: 992px) {
        padding: 30px 20px;
      }

      .product-title {
        font-size: 32px;
        color: #2c3e50;
        margin-bottom: 20px;
        font-weight: 700;
        border-left: 5px solid $primary-color;
        padding-left: 15px;

        @media (max-width: 576px) {
          font-size: 26px;
        }
      }

      .product-description {
        margin-bottom: 30px;
        padding-bottom: 20px;
        border-bottom: 1px solid #eee;

        p {
          font-size: 15px;
          color: #606266;
          line-height: 1.8;
          margin: 0;
        }
      }

      .info-grid {
        display: flex;
        flex-direction: column;
        gap: 25px;

        .info-item {
          .info-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 10px;

            i {
              font-size: 18px;
              color: $primary-color;
            }

            h4 {
              font-size: 16px;
              color: #34495e;
              margin: 0;
              font-weight: 600;
              position: relative;

              &::after {
                content: '';
                position: absolute;
                bottom: -4px;
                left: 0;
                width: 100%;
                height: 2px;
                background: rgba($primary-color, 0.2);
              }
            }
          }

          p {
            font-size: 14px;
            color: #606266;
            line-height: 1.7;
            margin: 0;
            padding-left: 26px;
          }
        }
      }
    }
  }

  .product-list-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    padding: 10px 0;

    @media (max-width: 768px) {
      padding: 0 10px;
    }

    .nav-btn {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: none;
      background: #fff;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      color: #606266;

      &:hover:not(:disabled) {
        background: $primary-color;
        color: #fff;
        transform: scale(1.1);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      i {
        font-size: 18px;
      }

      @media (max-width: 576px) {
        width: 32px;
        height: 32px;

        i {
          font-size: 14px;
        }
      }
    }

    .product-list-viewport {
      overflow: hidden;
      width: 840px;
      padding-top: 10px;
      @media (max-width: 992px) {
        width: 700px;
      }

      @media (max-width: 768px) {
        width: 560px;
      }

      @media (max-width: 576px) {
        width: 280px;
      }
    }

    .product-list-wrapper {
      display: flex;
      gap: 20px;
      transition: transform 0.4s ease;
      touch-action: pan-x;
      user-select: none;
      -webkit-user-select: none;

      @media (max-width: 576px) {
        gap: 10px;
      }
    }

    .product-thumb-item {
      flex-shrink: 0;
      cursor: pointer;
      text-align: center;
      transition: all 0.3s ease;
      width: 120px;

      @media (max-width: 576px) {
        width: 90px;
      }

      .thumb-img-box {
        width: 100%;
        aspect-ratio: 1 / 1;
        border-radius: 12px;
        overflow: hidden;
        border: 3px solid transparent;
        margin-bottom: 8px;
        background: #fff;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
        transition: all 0.3s ease;

        .el-image {
          width: 100%;
          height: 100%;
          transition: transform 0.3s ease;
        }
      }

      .thumb-name {
        font-size: 14px;
        color: #909399;
        transition: all 0.3s;

        @media (max-width: 576px) {
          font-size: 12px;
        }
      }

      &:hover {
        .thumb-img-box {
          border-color: rgba($primary-color, 0.5);
          transform: scale(1.05);
        }

        .thumb-name {
          color: $primary-color;
        }
      }

      &.active {
        .thumb-img-box {
          border-color: $primary-color;
          box-shadow: 0 0 0 4px rgba($primary-color, 0.2), 0 8px 20px rgba($primary-color, 0.3);
          transform: scale(1.08);

          .el-image {
            transform: scale(1.05);
          }
        }

        .thumb-name {
          color: $primary-color;
          font-weight: 700;
        }
      }
    }
  }

  .image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e0e5ec 0%, #d0d5dc 100%);
    color: #909399;
    font-size: 14px;
    text-align: center;
    padding: 10px;
  }
}

// 错误状态（确保样式正常）
.error-container {
  text-align: center;
  padding: 60px 20px;
  max-width: 600px;
  margin: 0 auto;
}

.error-icon {
  margin-bottom: 20px;
  color: $secondary-color-2;
}

.error-title {
  font-size: 1.5rem;
  color: $secondary-color-2;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  line-height: 1.5;
}

// 加载状态（确保居中显示）
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}
</style>
